import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import API from "../services/api";
import RowActions from "../components/RowActions";
import { useSnackbar } from "notistack";
import { useAuth } from "../context/AuthContext";
import { canRead } from "../constants/permissions";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const CustomerManagement = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyMeta, setHistoryMeta] = useState({ total: 0, page: 1, limit: 10 });
  const [historyLoading, setHistoryLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearchTerm,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      };
      const response = await API.get("/customers", { params });
      setCustomers(response.data.data || []);
      setTotalCount(
        response.data.meta && typeof response.data.meta.total === "number"
          ? response.data.meta.total
          : Array.isArray(response.data.data)
          ? response.data.data.length
          : 0
      );
    } catch (error) {
      enqueueSnackbar("Error fetching customers", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchTerm, sortConfig.key, sortConfig.direction, enqueueSnackbar]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, sortConfig.key, sortConfig.direction]);

  const handleStatusToggle = async (customer) => {
    const updatedStatus = !customer.is_active;
    try {
      await API.put(`/customers/${customer.id}`, { is_active: updatedStatus });
      enqueueSnackbar(`Customer ${updatedStatus ? "activated" : "deactivated"} successfully`, {
        variant: "success",
      });
      fetchCustomers();
    } catch (error) {
      enqueueSnackbar("Error updating customer status", { variant: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;

    try {
      await API.delete(`/customers/${customerToDelete.id}`);
      enqueueSnackbar("Customer deleted successfully", { variant: "success" });
      fetchCustomers();
    } catch (error) {
      enqueueSnackbar("Error deleting customer", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const fetchCustomerHistory = useCallback(
    async (customer, requestedPage = 1) => {
      if (!customer) return;

      setHistoryLoading(true);
      try {
        const response = await API.get(`/customers/${customer.id}/history`, {
          params: { page: requestedPage, limit: historyMeta.limit },
        });
        setHistoryEntries(response.data.data || []);
        setHistoryMeta(response.data.meta || { total: 0, page: requestedPage, limit: historyMeta.limit });
      } catch (error) {
        enqueueSnackbar("Error fetching customer history", { variant: "error" });
      } finally {
        setHistoryLoading(false);
      }
    },
    [enqueueSnackbar, historyMeta.limit]
  );

  const openHistoryDialog = async (customer) => {
    setHistoryCustomer(customer);
    setHistoryEntries([]);
    setHistoryMeta((current) => ({ ...current, total: 0, page: 1 }));
    setHistoryDialogOpen(true);
    await fetchCustomerHistory(customer, 1);
  };

  const HISTORY_FIELD_LABELS = {
    name: "Company",
    code: "Customer Code",
    host_name: "Host Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    city: "City",
    state: "State",
    is_active: "Status",
    isDeleted: "Deletion Status",
    plan_id: "Plan",
    status: "Subscription Status",
    application_module: "Module",
    config_key: "Configuration Key",
  };

  const isMissingValue = (value) => value === null || value === undefined || value === "";

  const displayHistoryValue = (field, value, entry, side) => {
    if (field === "plan_id") {
      return entry.plan_names?.[side] || (isMissingValue(value) ? "N/A" : String(value));
    }
    if (isMissingValue(value)) return "N/A";
    if (field === "is_active") return Number(value) === 1 || value === true ? "Active" : "Inactive";
    if (field === "isDeleted") return Number(value) === 1 || value === true ? "Deleted" : "Not deleted";
    if (field === "status") {
      const normalizedStatus = String(value).trim().toLowerCase();
      return normalizedStatus ? `${normalizedStatus[0].toUpperCase()}${normalizedStatus.slice(1)}` : "N/A";
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const historyValueColor = (field, value) => {
    if (isMissingValue(value)) return "default";
    if (field === "is_active") return Number(value) === 1 || value === true ? "green" : "red";
    if (field === "isDeleted") return Number(value) === 1 || value === true ? "red" : "green";
    if (field === "status") return String(value).toLowerCase() === "active" ? "green" : "orange";
    return "blue";
  };

  const renderHistoryChanges = (entry) => {
    const before = entry.before || {};
    const after = entry.after || {};
    const fields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    const changedFields = fields.filter(
      (field) =>
        field === "config_value_changed" ||
        (field !== "customer_id" && field !== "isDeleted" && before[field] !== after[field])
    );

    if (!changedFields.length) {
      return <Tag>No visible field changes</Tag>;
    }

    return (
      <Space wrap size={[6, 8]}>
        {changedFields.map((field) => {
          if (field === "config_value_changed") {
            return <Tag color="cyan" key={field}>Configuration value updated</Tag>;
          }

          const label = HISTORY_FIELD_LABELS[field] || field.replaceAll("_", " ");
          const beforeValue = displayHistoryValue(field, before[field], entry, "before");
          const afterValue = displayHistoryValue(field, after[field], entry, "after");
          const isCreate = entry.action === "create";

          if (isCreate) {
            return (
              <Tag color={historyValueColor(field, after[field])} key={field}>
                {label}: {afterValue}
              </Tag>
            );
          }

          return (
            <Space key={field} size={3}>
              <Tag color={historyValueColor(field, before[field])}>{label}: {beforeValue}</Tag>
              <span style={{ color: "#94a3b8" }}>→</span>
              <Tag color={historyValueColor(field, after[field])}>{afterValue}</Tag>
            </Space>
          );
        })}
      </Space>
    );
  };

  const canViewHistory = canRead(user, "customerHistory");

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: sortConfig.key === "name" ? `${sortConfig.direction}end` : null,
    },
    {
      title: "Host_Name",
      dataIndex: "host_name",
      key: "host_name",
      sorter: true,
      sortOrder: sortConfig.key === "host_name" ? `${sortConfig.direction}end` : null,
    },
    {
      title: "Customer Code",
      dataIndex: "code",
      key: "code",
      sorter: true,
      sortOrder: sortConfig.key === "code" ? `${sortConfig.direction}end` : null,
      render: (value) => value || "None",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      sortOrder: sortConfig.key === "email" ? `${sortConfig.direction}end` : null,
      render: (value) => value || "None",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (_, customer) => (
        <Space>
          <Switch checked={customer.is_active} onChange={() => handleStatusToggle(customer)} />
          <Tag color={customer.is_active ? "green" : "default"}>
            {customer.is_active ? "Active" : "Inactive"}
          </Tag>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, customer) => (
        <RowActions
          disabled={loading}
          items={[
            canViewHistory && {
              key: "history",
              label: "View history",
              icon: <EyeOutlined />,
              onClick: () =>
                navigate(`/customers/${customer.id}/history`, { state: { customer } }),
            },
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () =>
                navigate(`/customers/edit/${customer.id}`, {
                  state: { customer },
                }),
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                setCustomerToDelete(customer);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const emptyState = (
    <div style={{ padding: 48, textAlign: "center" }}>
      <ApartmentOutlined style={{ fontSize: 56, color: "rgba(100, 116, 139, 0.45)" }} />
      <Typography.Title level={4} style={{ marginTop: 16 }}>
        No customers found
      </Typography.Title>
      <Typography.Text type="secondary">
        {searchTerm ? "Clear the search to see all records." : "Add the first customer to get started."}
      </Typography.Text>
      <div style={{ marginTop: 20 }}>
        {searchTerm ? (
          <Button icon={<ClearOutlined />} onClick={() => setSearchTerm("")}>
            Clear search
          </Button>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/customers/add")}>
            Add customer
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card
      className="app-page-card"
      variant="borderless"
      styles={{ body: { padding: 24 } }}
    >
      <div className="page-toolbar">
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Customer Management
          </Typography.Title>
        </div>

        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchCustomers} loading={loading} />
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/customers/add")}
          >
            Add Customer
          </Button>
        </Space>
      </div>

      <Space style={{ width: "100%", marginBottom: 20 }} wrap>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          style={{ width: 300 }}
          disabled={loading}
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={customers}
        loading={loading}
        pagination={{
          current: page + 1,
          pageSize: rowsPerPage,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 25],
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage - 1);
            setRowsPerPage(nextPageSize);
          },
        }}
        locale={{ emptyText: emptyState }}
        onChange={(_, __, sorter) => {
          if (sorter?.field) {
            setSortConfig({
              key: sorter.field,
              direction: sorter.order === "descend" ? "desc" : "asc",
            });
          }
        }}
      />

      <Modal
        open={deleteDialogOpen}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setCustomerToDelete(null);
        }}
        onOk={handleDeleteConfirm}
        okText="Delete"
        okButtonProps={{ danger: true, loading }}
        title="Confirm delete"
      >
        <Typography.Text>
          Are you sure you want to delete customer <strong>{customerToDelete?.name}</strong>?
        </Typography.Text>
      </Modal>

      <Modal
        open={historyDialogOpen}
        onCancel={() => {
          setHistoryDialogOpen(false);
          setHistoryCustomer(null);
          setHistoryEntries([]);
        }}
        footer={null}
        width={1000}
        title={`Customer History${historyCustomer ? ` — ${historyCustomer.name}` : ""}`}
      >
        <Table
          rowKey="id"
          size="small"
          loading={historyLoading}
          dataSource={historyEntries}
          columns={[
            {
              title: "When",
              dataIndex: "occurred_at",
              key: "occurred_at",
              width: 180,
              render: (value) => (value ? new Date(value).toLocaleString() : "—"),
            },
            {
              title: "Activity",
              key: "activity",
              width: 170,
              render: (_, entry) => (
                <Space size={4} wrap>
                  <Tag color={entry.entity_type === "customer" ? "blue" : entry.entity_type === "subscription" ? "purple" : "gold"}>
                    {entry.entity_type?.replaceAll("_", " ")}
                  </Tag>
                  <Tag color={entry.action?.includes("delete") ? "red" : entry.action === "create" ? "green" : "blue"}>
                    {entry.action?.replaceAll("_", " ")}
                  </Tag>
                </Space>
              ),
            },
            {
              title: "Changed by",
              key: "actor",
              width: 220,
              render: (_, entry) => entry.actor_email || entry.actor_id || "System",
            },
            {
              title: "Details",
              key: "changes",
              render: (_, entry) => renderHistoryChanges(entry),
            },
          ]}
          pagination={{
            current: historyMeta.page,
            pageSize: historyMeta.limit,
            total: historyMeta.total,
            showSizeChanger: false,
            onChange: (nextPage) => fetchCustomerHistory(historyCustomer, nextPage),
          }}
          locale={{ emptyText: "No history has been recorded for this customer." }}
        />
      </Modal>
    </Card>
  );
};

export default CustomerManagement;
